import React from 'react'
import * as ReactDOM from 'react-dom'
import {merge} from '../../utils/util'
import {agCellStyle, presetCellStyles} from "../../utils/styles"
import {GridUtil} from "../../utils/GridUtil"
import loadable from "@loadable/component"
import {ClipboardModule} from '@ag-grid-enterprise/clipboard'
import {RangeSelectionModule} from '@ag-grid-enterprise/range-selection'

const AgGridReact = loadable(() => import(/* webpackChunkName: "agGrid" */ "../../splitting/agGridSetup"))

const getAggregateStyles = (specifiedStyles, presetStyles) => {
  return (specifiedStyles && specifiedStyles.length)
      ? specifiedStyles.map(x => presetStyles[x]).reduce((x, y) => merge(x, y))
      : {}
}

const getColumnStyles = (columnStyles) => {
  const aggregateStyles = getAggregateStyles(columnStyles, presetCellStyles)
  return merge(agCellStyle, aggregateStyles)
}

function processCellForClipboard(params) {

  const value = params.value
  if (value) {
    if (typeof value == 'string') {
      return value
    }
    if (value.first) {
      return value.first.toString()
    }
  }
  return null
}

export class AgDetailsTable extends React.Component {
  static agRenderer(gridObject) {
    const value = gridObject.value
    if (value && Object.getOwnPropertyDescriptor(value, 'props')) {
      const name = value.type.name
      if (name === 'Link') {
        return `<a href=#${value.props.to} className=${value.props.className}>${value.props.children}</a>`
      } else {
        const div = document.createElement('div')
        ReactDOM.render(gridObject.value, div)
        return div
      }
    }
    return gridObject.value
  }

  static getAdditionalColumnAttributes(col, showColumnMenuTab) {
    let attributes = {
      menuTabs: showColumnMenuTab ? ['filterMenuTab', 'columnsMenuTab'] : ['filterMenuTab'],  // disable 'generalMenuTab' for now
      comparator: GridUtil.genericCompareBlankAlwaysLast
    }

    if (col.columnWidth) {
      attributes.width = col.columnWidth
    }

    if (col.suppressSizeToFit) {
      attributes.suppressSizeToFit = col.suppressSizeToFit
    }

    if (col.agColumnProperties) {
      attributes = merge(attributes, col.agColumnProperties)
    }

    // allow some attributes to be rendered by the caller (Pair, Link) for now
    if (!attributes.cellRendererFramework && !attributes.cellRenderer) {
      attributes.cellRenderer = AgDetailsTable.agRenderer
    }

    if (col.checkboxSelection) {
      attributes.checkboxSelection = col.checkboxSelection
    }

    if (col.headerCheckboxSelection) {
      attributes.headerCheckboxSelection = col.headerCheckboxSelection
    }

    if (col.headerCheckboxSelectionFilteredOnly) {
      attributes.headerCheckboxSelectionFilteredOnly = col.headerCheckboxSelectionFilteredOnly
    }

    if (col.suppressMenu) {
      attributes.suppressMenu = col.suppressMenu
    }

    if (col.sortable) {
      attributes.sortable = col.sortable
    }

    if (col.pinned) {
      attributes.pinned = col.pinned
    }

    if (col.cellStyle) {
      attributes.cellStyle = col.cellStyle
    }
    if (col.autoHeight) {
      attributes.autoHeight = col.autoHeight
    }
    if (col.enableRowGroup) {
      attributes.enableRowGroup = col.enableRowGroup
    }
    if (col.rowGroup) {
      attributes.rowGroup = col.rowGroup
    }
    if (col.keyCreator) {
      attributes.keyCreator = col.keyCreator
    }
    if (col.userData) {
      attributes.userData = col.userData
    }
    return attributes
  }

  static defaultRowHeight() {
    return 50
  }

  agConvertColumnDefs(columns) {
    return Object.keys(columns).map((fieldName) => {
      const column = columns[fieldName]
      return this.convertColumnDef(column, fieldName)
    })
  }

  convertColumnDef(column, fieldName) {
    if (Object.getOwnPropertyDescriptor(column, 'children')) {
      return merge(column, {children: this.agConvertColumnDefs(column.children)})
    }
    const showColumnMenuTab = this.props.showColumnMenuTab
    return merge({
      headerName: column.headerName,
      field: fieldName,
      cellStyle: getColumnStyles(column.columnStyles)
    }, AgDetailsTable.getAdditionalColumnAttributes(column, showColumnMenuTab))
  }

  render() {
    const {
      columns,
      rowsData,
      onGridReady,
      onGridMount,
      height,
      rowHeightFn,
      rowHeight,
      enableSingleRowSelection,
      enableMultiRowSelection,
      onCellClicked,
      onRowSelected,
      topMargin,
      rowEdit,
      onRowEditingStarted,
      onRowEditingStopped,
      onRowValueChanged,
      suppressCellSelection,
      headerHeight,
      groupHeaderHeight,
      rowClassRules,
      suppressHorizontalScroll,
      suppressRowClickSelection,
      suppressRowTransform,
      deltaRowDataMode,
      getRowNodeId,
      gridAutoHeight,
      onCellMouseOver,
      onCellMouseOut,
      editMode,
      grouping,
      autoGroupColumnDef,
      onColumnRowGroupChanged,
      groupUseEntireRow,
      groupRowRenderer,
      isRowSelectable,
      onCellValueChanged,
      autoSizeColumns
    } = this.props

    let getRowHeight = null
    let rowHeightValue = null
    if (rowHeightFn) {
      getRowHeight = rowHeightFn
    } else {
      rowHeightValue = rowHeight || AgDetailsTable.defaultRowHeight()
    }

    let editType = ''
    if (rowEdit === true) { // may be true/false or null
      editType = 'fullRow'
    }

    const rowSelection = enableMultiRowSelection ? 'multiple' : enableSingleRowSelection ? 'single' : ''

    const noRowsTemplate = '<span style="padding: 10px; border: 1px solid #444; border-radius: 5px">No Rows</span>'
    const loadingRowsTemplate = '<img alt="loading..." src="images/loading2.gif" style="height: 40px"/>'
    let domLayout = 'normal'
    if (gridAutoHeight === true) {
      domLayout = 'autoHeight'
    }
    const handleAutoSize = autoSizeColumns
        ? (params) => params.columnApi.autoSizeAllColumns(false)
        : undefined

    const grid = (
        <AgGridReact
            // properties
            columnDefs={this.agConvertColumnDefs(columns)}
            rowData={rowsData}
            ref={onGridMount}
            //for ag-grid row grouping
            suppressDragLeaveHidesColumns={grouping}
            suppressMakeColumnVisibleAfterUnGroup={grouping}
            rowGroupPanelShow={grouping ? 'always' : 'never'}
            autoGroupColumnDef={autoGroupColumnDef}
            onColumnRowGroupChanged={onColumnRowGroupChanged}
            suppressPropertyNamesCheck={true}

            // properties (cannot use styles for these)
            getRowHeight={getRowHeight}
            rowHeight={rowHeightValue}
            headerHeight={headerHeight || 70}
            groupHeaderHeight={groupHeaderHeight || 30}
            defaultColDef={{resizable: true, sortable: true, filter: true}}
            overlayNoRowsTemplate={rowsData ? noRowsTemplate : loadingRowsTemplate}
            suppressCellSelection={suppressCellSelection ? suppressCellSelection : false}
            enableRangeSelection={true}
            enableCellTextSelection={true}
            rowSelection={rowSelection}
            processCellForClipboard={processCellForClipboard}
            onGridReady={onGridReady}
            onCellClicked={onCellClicked}
            onCellMouseOver={onCellMouseOver}
            onCellMouseOut={onCellMouseOut}
            onRowSelected={onRowSelected}
            onRowEditingStarted={onRowEditingStarted}
            onRowEditingStopped={onRowEditingStopped}
            onRowValueChanged={onRowValueChanged}
            onCellValueChanged={onCellValueChanged}
            editType={editType}
            isRowSelectable={isRowSelectable}
            rowClassRules={rowClassRules}
            deltaRowDataMode={deltaRowDataMode}
            getRowNodeId={getRowNodeId}
            domLayout={domLayout}
            suppressHorizontalScroll={suppressHorizontalScroll}
            suppressRowClickSelection={suppressRowClickSelection}
            suppressRowTransform={suppressRowTransform}
            groupUseEntireRow={groupUseEntireRow}
            groupRowRendererFramework={groupRowRenderer}
            modules={[ClipboardModule, RangeSelectionModule]}
            onFirstDataRendered={handleAutoSize}
            onRowDataUpdated={handleAutoSize}
        />
    )
    let style = null
    if (gridAutoHeight === false || gridAutoHeight === undefined) {
      if (height) {
        style = {height: height, overflowX: 'auto'}
      } else {
        style = {height: 'calc(100vh - ' + (topMargin || 79) + 'px)'}
      }
    }
    return (
        <div id="grid" className={editMode ? 'ag-lalb editMode' : 'ag-lalb'} style={style}>
          {grid}
        </div>
    )
  }
}